/**
 * Actor Security Hook - Life-Critical Access Control
 * 
 * Manages emergency access, security levels, and compliance validation
 * for life-critical workflow operations.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  ActorSecurity,
  SecurityValidationResult,
  SecurityValidationService,
  SecurityAuditContext,
  EmergencyAccessEvent
} from '../types/security';

// GraphQL queries (will be implemented in GraphQL layer)
const GET_ACTOR_SECURITY = gql`
  query GetActorSecurity($actorId: String!, $tenantId: String!) {
    actor(id: $actorId, tenantId: $tenantId) {
      security {
        actorId
        securityLevel
        emergencyAccess
        lastSecurityReview
        certificationLevel
        emergencyAccessLog {
          eventId
          accessedAt
          reason
          duration
          systemsAccessed
        }
        mfaEnabled
        mfaVerifiedAt
        biometricEnabled
        biometricVerifiedAt
      }
    }
  }
`;

const VALIDATE_SECURITY = gql`
  mutation ValidateFormDataSecurity(
    $data: JSON!, 
    $actorId: String!, 
    $context: SecurityAuditContextInput!
  ) {
    validateFormDataSecurity(data: $data, actorId: $actorId, context: $context) {
      isValid
      violations {
        violationId
        severity
        type
        description
        recommendedAction
      }
      riskScore
      recommendedActions
      complianceStatus {
        hipaa
        sox
        fda21cfr
      }
      requiresEmergencyOverride
      lifeCriticalChecks {
        patientSafetyValidated
        clinicalWorkflowApproved
        emergencyProtocolsActive
      }
    }
  }
`;

interface UseActorSecurityOptions {
  actorId: string;
  tenantId: string;
  autoValidate?: boolean;
}

interface UseActorSecurityReturn {
  // Current security state
  emergencyAccess: boolean;
  securityLevel: ActorSecurity['securityLevel'];
  actorId: string;
  
  // Security validation
  validateFormDataSecurity: (data: any) => Promise<SecurityValidationResult>;
  
  // Loading and error states
  loading: boolean;
  error: Error | null;
  
  // Security data
  securityData: ActorSecurity | null;
  
  // Emergency access management
  requestEmergencyAccess: (reason: string, systems: string[]) => Promise<void>;
  revokeEmergencyAccess: () => Promise<void>;
  
  // MFA and biometric verification
  verifyMFA: (code: string) => Promise<boolean>;
  verifyBiometric: () => Promise<boolean>;
  
  // Compliance status
  isCompliant: boolean;
  complianceIssues: string[];
}

export function useActorSecurity({
  actorId,
  tenantId,
  autoValidate = true
}: UseActorSecurityOptions): UseActorSecurityReturn {
  
  const [validationCache, setValidationCache] = useState<Map<string, SecurityValidationResult>>(new Map());
  
  // Fetch actor security data
  // Fetch actor security data from the backend
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_ACTOR_SECURITY, {
    variables: { actorId, tenantId },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    // Use cache-first to prevent continuous retries during development
    fetchPolicy: 'cache-first',
    // Skip query if backend doesn't support this operation yet
    skip: true // TODO: Enable when backend implements GET_ACTOR_SECURITY
  });

  // Security validation mutation
  const [validateSecurityMutation] = useMutation<Record<string, any>>(VALIDATE_SECURITY);

  const securityData = useMemo(() => data?.actor?.security || null, [data]);

  // Security validation function
  const validateFormDataSecurity = useCallback(async (formData: any): Promise<SecurityValidationResult> => {
    const cacheKey = JSON.stringify({ formData, actorId, timestamp: Date.now() });
    
    // Check cache first (but only for non-life-critical operations)
    const cached = validationCache.get(cacheKey);
    const isLifeCritical = formData?.metadata?.isLifeCritical || false;
    
    if (cached && !isLifeCritical) {
      return cached;
    }

    try {
      // Create audit context
      const context: SecurityAuditContext = {
        actorId,
        tenantId,
        sessionId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        operationType: 'create', // Infer from form data
        resourceType: formData?.type || 'workflow_form',
        timestamp: new Date().toISOString(),
        ipAddress: 'client', // Would be populated by backend
        userAgent: navigator.userAgent,
        isLifeCritical,
        patientId: formData?.patientId,
        clinicalContext: formData?.clinicalContext
      };

      const { data: validationData } = await validateSecurityMutation({
        variables: {
          data: formData,
          actorId,
          context
        }
      });

      const result = validationData?.validateFormDataSecurity;
      
      // Cache result for non-life-critical operations only
      if (!isLifeCritical) {
        setValidationCache(prev => new Map(prev.set(cacheKey, result)));
      }

      return result;
    } catch (error) {
      console.error('Security validation failed:', error);
      // Fail-safe: Return restrictive validation result
      return {
        isValid: false,
        violations: [{
          violationId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          severity: 'critical',
          type: 'unauthorized_access',
          description: 'Security validation service unavailable',
          recommendedAction: 'Contact system administrator immediately',
          complianceImpact: ['HIPAA', 'SOX', 'FDA 21 CFR Part 11']
        }],
        riskScore: 100,
        recommendedActions: ['Block all access until security validation is restored'],
        complianceStatus: {
          hipaa: false,
          sox: false,
          fda21cfr: false
        },
        requiresEmergencyOverride: true,
        lifeCriticalChecks: {
          patientSafetyValidated: false,
          clinicalWorkflowApproved: false,
          emergencyProtocolsActive: false
        }
      };
    }
  }, [actorId, tenantId, validateSecurityMutation, validationCache]);

  // Emergency access management
  const requestEmergencyAccess = useCallback(async (reason: string, systems: string[]) => {
    // Implementation would call emergency access mutation
    console.log('Emergency access requested:', { reason, systems, actorId });
    // Refetch security data after emergency access change
    await refetch();
  }, [actorId, refetch]);

  const revokeEmergencyAccess = useCallback(async () => {
    // Implementation would call revoke emergency access mutation
    console.log('Emergency access revoked:', { actorId });
    await refetch();
  }, [actorId, refetch]);

  // MFA verification
  const verifyMFA = useCallback(async (code: string): Promise<boolean> => {
    // Implementation would verify MFA code
    console.log('MFA verification attempted:', { actorId, code: '***' });
    return true; // Placeholder
  }, [actorId]);

  // Biometric verification
  const verifyBiometric = useCallback(async (): Promise<boolean> => {
    // Implementation would trigger biometric verification
    console.log('Biometric verification requested:', { actorId });
    return true; // Placeholder
  }, [actorId]);

  // Compliance status calculation
  const { isCompliant, complianceIssues } = useMemo(() => {
    if (!securityData) {
      return { isCompliant: false, complianceIssues: ['Security data not loaded'] };
    }

    const issues: string[] = [];
    
    // Check security review currency (must be within 90 days for life-critical)
    const reviewDate = new Date(securityData.lastSecurityReview);
    const daysSinceReview = (Date.now() - reviewDate.getTime()) / (1000 * 60 * 60 * 24);
    
    if (securityData.securityLevel === 'life-critical' && daysSinceReview > 90) {
      issues.push('Security review overdue for life-critical access');
    }

    // Check MFA requirement
    if (securityData.securityLevel !== 'standard' && !securityData.mfaEnabled) {
      issues.push('Multi-factor authentication required for elevated access');
    }

    // Check biometric requirement for life-critical
    if (securityData.securityLevel === 'life-critical' && !securityData.biometricEnabled) {
      issues.push('Biometric verification required for life-critical access');
    }

    return {
      isCompliant: issues.length === 0,
      complianceIssues: issues
    };
  }, [securityData]);

  return {
    emergencyAccess: securityData?.emergencyAccess ?? false,
    securityLevel: securityData?.securityLevel ?? 'standard',
    actorId,
    validateFormDataSecurity,
    loading,
    error: error || null,
    securityData,
    requestEmergencyAccess,
    revokeEmergencyAccess,
    verifyMFA,
    verifyBiometric,
    isCompliant,
    complianceIssues
  };
}